import {
  type ComputedRef,
  type Ref,
  computed,
  onBeforeUnmount,
  shallowRef,
  watch,
} from 'vue'

import { useElementSize } from '@vueuse/core'

import { useStickyBarBottomOffset } from '~/composables/useStickyBarBottomOffset'

/**
 * Wires any bottom `RevStickyBar` instance into the shared
 * `useStickyBarBottomOffset` state, so other bottom-anchored UI (e.g. the
 * shopping assistant FAB, mounted app-wide) can clear its actual rendered
 * height instead of guessing a fixed offset per page.
 *
 * `RevStickyBar` renders its content in a `position: fixed` child, which
 * contributes zero height to its own `$el` (a fixed box is out of normal
 * flow, so its non-fixed parent never grows to fit it). Measure that fixed
 * child directly instead. It only exists in the DOM while `isVisible` is
 * true. Measured with `box: 'border-box'` since `RevStickyBar`'s content div
 * carries its own padding/border, which content-box would otherwise exclude.
 *
 * This can't be a `computed`: reading `$el.firstElementChild` is a plain DOM
 * access, not tracked by Vue's reactivity, so a computed's cached value is
 * only as fresh as its last *reactive* dependency change (`isVisible`/
 * `stickyBarRef.value`) — and that dependency can be invalidated and re-read
 * before `RevStickyBar`'s own `v-if` has actually patched the content node
 * into the DOM, permanently caching `null`. A `flush: 'post'` watcher instead
 * re-reads the DOM after Vue has fully patched (including nested children)
 * for that same dependency change. The source getter reads
 * `stickyBarRef.value` itself (not just `isVisible`) so mounting with
 * `isVisible` already `true` also re-triggers it once `stickyBarRef`
 * settles, instead of only firing on later `isVisible` toggles.
 */
export function useStickyBarBottomHeightLogic(
  stickyBarRef: Ref<{ $el?: HTMLElement | null } | null>,
  isVisible: Ref<boolean> | ComputedRef<boolean> = computed(() => true),
) {
  const { setStickyBarBottomHeight } = useStickyBarBottomOffset()

  const stickyBarContentEl = shallowRef<HTMLElement | null>(null)
  watch(
    () => (isVisible.value ? stickyBarRef.value : null),
    (barInstance) => {
      stickyBarContentEl.value =
        (barInstance?.$el?.firstElementChild as HTMLElement | null) ?? null
    },
    { immediate: true, flush: 'post' },
  )

  const { height: stickyBarHeight, stop: stopObserver } = useElementSize(
    stickyBarContentEl,
    undefined,
    { box: 'border-box' },
  )
  // `useElementSize` resets `stickyBarHeight` to 0 on its own once
  // `stickyBarContentEl` goes null (e.g. `isVisible` turning false), so this
  // single watch already covers both the live-measurement and hide cases.
  watch(stickyBarHeight, setStickyBarBottomHeight, { immediate: true })
  onBeforeUnmount(() => {
    stopObserver()
    setStickyBarBottomHeight(0)
  })
}
